import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { Input } from '@components/Input';
import { Select } from '@components/Select';
import { useTranslation, useYupValidationResolver } from '@hooks';
import { ConversationStatus, ConversationProperty } from '@constants/conversations';
import { Modal, Content } from './styles';

const EditBulkModal = ({ open, setOpen, selectedCount, onSubmit }) => {
    const { t } = useTranslation();

    const EditBulkSchema = yup.object().shape({
        property: yup.number().required('validation.required'),
        status: yup.number().when('property', {
            is: value => value === ConversationProperty.Status,
            then: schema => schema.required('validation.required'),
            otherwise: schema => schema.notRequired(),
        }),
    });

    const resolver = useYupValidationResolver(EditBulkSchema);

    const {
        setValue,
        control,
        watch,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm({
        mode: 'all',
        resolver,
        defaultValues: {
            conversations: t('InboxPage.conversationSelected', { count: selectedCount }),
            property: ConversationProperty.Status,
            status: '',
        },
    });

    const selectedProperty = watch('property');

    return (
        <Modal
            fullScreen
            isOpen={open}
            handleClose={() => setOpen(false)}
            isDirty={isDirty}
            title={t('InboxPage.editConversations')}
            onPrimaryAction={handleSubmit(onSubmit)}
            primaryActionText={t('buttons.saveChanges')}
            primaryActionDisabled={isSubmitting || !isValid || !isDirty}
        >
            <Content>
                <Controller
                    control={control}
                    name="conversations"
                    render={({ field, ref }) => (
                        <Input ref={ref} inputProps={{ ...field }} label={t('Header.contacts')} fullWidth disabled />
                    )}
                />
                <Controller
                    control={control}
                    name="property"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Select
                            {...field}
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            onChange={({ target: { value } }) => {
                                setValue('property', value, { shouldDirty: true });
                            }}
                            options={[{ label: t('common.status'), value: ConversationProperty.Status }]}
                            label={t('InboxPage.selectProperty')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            required
                        />
                    )}
                />
                {selectedProperty === ConversationProperty.Status && (
                    <Controller
                        control={control}
                        name="status"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Select
                                {...field}
                                ref={ref}
                                inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                onChange={({ target: { value } }) => {
                                    setValue('status', +value, { shouldDirty: true });
                                }}
                                options={[
                                    { label: t('common.open'), value: ConversationStatus.Open },
                                    { label: t('InboxPage.resolved'), value: ConversationStatus.Closed },
                                ]}
                                label={t('common.status')}
                                fullWidth
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                                required
                            />
                        )}
                    />
                )}
            </Content>
        </Modal>
    );
};

export default EditBulkModal;
